import React, { useEffect, useState } from 'react';
import { Button, Card, Badge, StatusDot, Avatar, useToast } from '../../components/ui';
import { teamsApi, agentsApi, skillsApi } from '../../api/client';
import { useAppContext } from '../../store/AppContext';
import type { Agent, Team, Skill, TeamTemplate } from '../../api/types';

// ─── LLM Model Options ────────────────────────────────────────────────────
const LLM_MODEL_OPTIONS = [
  { label: 'Claude Sonnet 4 (default)', provider: 'claude', model: 'claude-sonnet-4-20250514' },
  { label: 'Claude Opus 4', provider: 'claude', model: 'claude-opus-4-20250514' },
  { label: 'GPT-4o', provider: 'openai', model: 'gpt-4o' },
] as const;

const DEFAULT_LLM_VALUE = `claude|claude-sonnet-4-20250514`;

// ─── Agent config for wizard ──────────────────────────────────────────────
interface AgentConfig {
  displayName: string;
  role: string;
  llmValue: string; // "provider|model"
  description: string;
}

interface Props {
  projectId: string;
}

export const TeamsPanel: React.FC<Props> = ({ projectId }) => {
  const { teams, setTeams, agents, setAgents } = useAppContext();
  const { showToast } = useToast();
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agentTab, setAgentTab] = useState<'memory' | 'skills'>('memory');
  const [memory, setMemory] = useState('');
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [allSkills, setAllSkills] = useState<Skill[]>([]);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<TeamTemplate | null>(null);
  const [templates, setTemplates] = useState<TeamTemplate[]>([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);
  const [agentConfigs, setAgentConfigs] = useState<AgentConfig[]>([]);

  useEffect(() => {
    teamsApi.list(projectId).then((r) => { if (r.ok) setTeams(Array.isArray(r.data) ? r.data : ((r.data as { items?: Team[] }).items ?? [])); });
    agentsApi.list(projectId).then((r) => { if (r.ok) setAgents(Array.isArray(r.data) ? r.data : ((r.data as { items?: Agent[] }).items ?? [])); });
    skillsApi.list().then((r) => { if (r.ok) setAllSkills(Array.isArray(r.data) ? r.data : ((r.data as { items?: Skill[] }).items ?? [])); });
    teamsApi.templates().then((r) => { if (r.ok) setTemplates(Array.isArray(r.data) ? r.data : ((r.data as { items?: TeamTemplate[] }).items ?? [])); });
  }, [projectId, setTeams, setAgents]);

  const loadAgentDetails = async (agent: Agent) => {
    setSelectedAgent(agent);
    setAgentTab('memory');
    setMemoryLoading(true);
    const res = await agentsApi.getMemory(agent.id);
    if (res.ok) setMemory(res.data.content ?? '');
    else setMemory('');
    setMemoryLoading(false);

    // Load skills (agent.skills or derive from allSkills)
    setSkills(agent.skills ?? []);
  };

  const handleSaveMemory = async () => {
    if (!selectedAgent) return;
    const res = await agentsApi.updateMemory(selectedAgent.id, memory);
    if (res.ok) {
      showToast('Memory saved', 'success');
    } else {
      showToast('Failed to save memory', 'error');
    }
  };

  const handleClearMemory = async () => {
    if (!selectedAgent) return;
    const res = await agentsApi.clearMemory(selectedAgent.id);
    if (res.ok) {
      setMemory('');
      showToast('Memory cleared', 'success');
    } else {
      showToast('Failed to clear memory', 'error');
    }
  };

  const handleInstallSkill = async (skillId: string) => {
    if (!selectedAgent) return;
    const res = await skillsApi.install(selectedAgent.id, skillId);
    if (res.ok) {
      setSkills((prev) => {
        const sk = allSkills.find((s) => s.id === skillId);
        return sk ? [...prev, sk] : prev;
      });
      const skillName = allSkills.find((s) => s.id === skillId)?.name ?? 'Skill';
      showToast(`${skillName} installed`, 'success');
    } else {
      showToast('Failed to install skill', 'error');
    }
  };

  const handleCreateTeam = async () => {
    setCreating(true);
    const name = newTeamName.trim() || (selectedTemplate?.name ?? 'New Team');
    let res;
    if (selectedTemplate) {
      res = await teamsApi.fromTemplate(projectId, selectedTemplate.id, name);
    } else {
      res = await teamsApi.create(projectId, name);
    }
    if (!res.ok) {
      setCreating(false);
      showToast('Failed to create team', 'error');
      return;
    }
    const team = res.data;

    // Create agents from wizard config
    if (agentConfigs.length > 0) {
      for (const cfg of agentConfigs) {
        if (!cfg.displayName.trim()) continue;
        const [llmProvider, llmModel] = cfg.llmValue.split('|');
        await agentsApi.create({
          displayName: cfg.displayName.trim(),
          role: cfg.role.trim() || 'Agent',
          projectId,
          teamId: team.id,
          llmProvider,
          llmModel,
          description: cfg.description.trim() || undefined,
        });
      }
      const agentsRes = await agentsApi.list(projectId);
      if (agentsRes.ok) setAgents(Array.isArray(agentsRes.data) ? agentsRes.data : ((agentsRes.data as { items?: Agent[] }).items ?? []));
    }

    setCreating(false);
    setTeams([...teams, team]);
    setShowCreateTeam(false);
    setWizardStep(0);
    setSelectedTemplate(null);
    setNewTeamName('');
    setAgentConfigs([]);
    showToast(`Team "${name}" created`, 'success');
  };

  const handleWizardClose = () => {
    setShowCreateTeam(false);
    setWizardStep(0);
    setSelectedTemplate(null);
    setNewTeamName('');
    setAgentConfigs([]);
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Teams list */}
      <div
        style={{
          width: 300,
          borderRight: '1px solid var(--border)',
          overflow: 'auto',
          padding: '16px',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h2 style={{ flex: 1, fontSize: 16, fontWeight: 700 }}>Teams & Agents</h2>
          <Button size="sm" onClick={() => { setShowCreateTeam(true); setWizardStep(0); }}>
            + Team
          </Button>
        </div>

        {teams.length === 0 && (
          <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: 32 }}>
            No teams yet
          </div>
        )}

        {teams.map((team: Team) => {
          const teamAgents = agents.filter((a) => a.teamId === team.id);
          return (
            <div key={team.id} style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 6,
                }}
              >
                {team.name}
              </div>
              {teamAgents.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', paddingLeft: 8 }}>
                  No agents
                </div>
              )}
              {teamAgents.map((agent) => (
                <AgentRow
                  key={agent.id}
                  agent={agent}
                  selected={selectedAgent?.id === agent.id}
                  onClick={() => loadAgentDetails(agent)}
                />
              ))}
            </div>
          );
        })}

        {/* Unassigned agents */}
        {(() => {
          const unassigned = agents.filter((a) => !a.teamId);
          if (unassigned.length === 0) return null;
          return (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 6,
                }}
              >
                Unassigned
              </div>
              {unassigned.map((agent) => (
                <AgentRow
                  key={agent.id}
                  agent={agent}
                  selected={selectedAgent?.id === agent.id}
                  onClick={() => loadAgentDetails(agent)}
                />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Agent detail panel */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {!selectedAgent ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'var(--text-muted)',
              fontSize: 14,
            }}
          >
            Select an agent to view details
          </div>
        ) : (
          <AgentDetailPanel
            agent={selectedAgent}
            tab={agentTab}
            setTab={setAgentTab}
            memory={memory}
            setMemory={setMemory}
            memoryLoading={memoryLoading}
            onSaveMemory={handleSaveMemory}
            onClearMemory={handleClearMemory}
            skills={skills}
            allSkills={allSkills}
            onInstallSkill={handleInstallSkill}
          />
        )}
      </div>

      {/* Create team wizard modal */}
      {showCreateTeam && (
        <TeamWizardModal
          step={wizardStep}
          setStep={setWizardStep}
          templates={templates}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          teamName={newTeamName}
          setTeamName={setNewTeamName}
          agentConfigs={agentConfigs}
          setAgentConfigs={setAgentConfigs}
          creating={creating}
          onConfirm={handleCreateTeam}
          onClose={handleWizardClose}
        />
      )}
    </div>
  );
};

function AgentRow({
  agent,
  selected,
  onClick,
}: {
  agent: Agent;
  selected: boolean;
  onClick: () => void;
}) {
  const initials = agent.displayName.slice(0, 2).toUpperCase();
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 'var(--radius)',
        background: selected ? 'var(--primary-light)' : 'transparent',
        border: selected ? '1px solid #C7D2FE' : '1px solid transparent',
        cursor: 'pointer',
        textAlign: 'left',
        marginBottom: 2,
      }}
    >
      <div style={{ position: 'relative' }}>
        <Avatar initials={initials} size="sm" color="#4F46E5" />
        <div style={{ position: 'absolute', bottom: -1, right: -1 }}>
          <StatusDot status={agent.status} size={7} />
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: selected ? 'var(--primary)' : 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {agent.displayName}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{agent.role}</div>
      </div>
      <StatusBadge status={agent.status} />
    </button>
  );
}

function StatusBadge({ status }: { status: Agent['status'] }) {
  const map: Record<Agent['status'], { variant: 'success' | 'warning' | 'error' | 'neutral'; label: string }> = {
    online: { variant: 'success', label: 'Online' },
    offline: { variant: 'neutral', label: 'Offline' },
    thinking: { variant: 'warning', label: 'Thinking' },
    busy: { variant: 'error', label: 'Busy' },
  };
  const { variant, label } = map[status];
  return <Badge variant={variant}>{label}</Badge>;
}

function AgentDetailPanel({
  agent,
  tab,
  setTab,
  memory,
  setMemory,
  memoryLoading,
  onSaveMemory,
  onClearMemory,
  skills,
  allSkills,
  onInstallSkill,
}: {
  agent: Agent;
  tab: 'memory' | 'skills';
  setTab: (t: 'memory' | 'skills') => void;
  memory: string;
  setMemory: (v: string) => void;
  memoryLoading: boolean;
  onSaveMemory: () => void;
  onClearMemory: () => void;
  skills: Skill[];
  allSkills: Skill[];
  onInstallSkill: (skillId: string) => void;
}) {
  const initials = agent.displayName.slice(0, 2).toUpperCase();

  return (
    <div style={{ padding: '24px' }}>
      {/* Agent header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <div style={{ position: 'relative' }}>
          <Avatar initials={initials} size="lg" color="#4F46E5" />
          <div style={{ position: 'absolute', bottom: 0, right: 0 }}>
            <StatusDot status={agent.status} size={10} />
          </div>
        </div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700 }}>{agent.displayName}</h2>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{agent.role}</div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <StatusBadge status={agent.status} />
        </div>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 2,
          marginBottom: 20,
          borderBottom: '1px solid var(--border)',
        }}
      >
        {(['memory', 'skills'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 16px',
              background: 'none',
              border: 'none',
              borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
              color: tab === t ? 'var(--primary)' : 'var(--text-secondary)',
              fontWeight: tab === t ? 600 : 400,
              cursor: 'pointer',
              fontSize: 14,
              marginBottom: -1,
            }}
          >
            {t === 'memory' ? 'Memory' : 'Skills'}
          </button>
        ))}
      </div>

      {/* Memory tab */}
      {tab === 'memory' && (
        <div>
          {memoryLoading ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading memory…</div>
          ) : (
            <>
              <textarea
                value={memory}
                onChange={(e) => setMemory(e.target.value)}
                placeholder="Agent memory is empty."
                rows={10}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: 13,
                  fontFamily: 'monospace',
                  outline: 'none',
                  resize: 'vertical',
                  color: 'var(--text)',
                  background: 'var(--bg-secondary)',
                }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <Button size="sm" onClick={onSaveMemory}>
                  Save memory
                </Button>
                <Button size="sm" variant="danger" onClick={onClearMemory}>
                  Clear memory
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Skills tab */}
      {tab === 'skills' && (
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
            Installed Skills ({skills.length})
          </h3>
          {skills.length === 0 && (
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              No skills installed
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 20 }}>
            {skills.map((s) => (
              <Badge key={s.id} variant="info">
                {s.name}
              </Badge>
            ))}
          </div>

          {allSkills.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                Available Skills
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allSkills
                  .filter((s) => !skills.find((i) => i.id === s.id))
                  .map((s) => (
                    <div
                      key={s.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '10px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        background: 'white',
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</div>
                        {s.description && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                            {s.description}
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="secondary" onClick={() => onInstallSkill(s.id)}>
                        Install
                      </Button>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TeamWizardModal({
  step,
  setStep,
  templates,
  selectedTemplate,
  setSelectedTemplate,
  teamName,
  setTeamName,
  agentConfigs,
  setAgentConfigs,
  creating,
  onConfirm,
  onClose,
}: {
  step: number;
  setStep: (s: number) => void;
  templates: TeamTemplate[];
  selectedTemplate: TeamTemplate | null;
  setSelectedTemplate: (t: TeamTemplate | null) => void;
  teamName: string;
  setTeamName: (v: string) => void;
  agentConfigs: AgentConfig[];
  setAgentConfigs: (cfg: AgentConfig[]) => void;
  creating: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const STEP_LABELS = ['Template', 'Configure', 'Confirm'];

  const addAgent = () =>
    setAgentConfigs([
      ...agentConfigs,
      { displayName: '', role: '', llmValue: DEFAULT_LLM_VALUE, description: '' },
    ]);

  const removeAgent = (idx: number) =>
    setAgentConfigs(agentConfigs.filter((_, i) => i !== idx));

  const updateAgent = (idx: number, patch: Partial<AgentConfig>) =>
    setAgentConfigs(agentConfigs.map((cfg, i) => (i === idx ? { ...cfg, ...patch } : cfg)));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 'var(--radius-lg)',
          padding: 28,
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          {STEP_LABELS.map((label, i) => (
            <React.Fragment key={i}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: i > step ? 0.4 : 1,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: i <= step ? 'var(--primary)' : 'var(--border)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {i < step ? '✓' : i + 1}
                </div>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: i === step ? 600 : 400,
                    color: i === step ? 'var(--text)' : 'var(--text-muted)',
                  }}
                >
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0: Template */}
        {step === 0 && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              Choose a template
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              <div
                onClick={() => setSelectedTemplate(null)}
                style={{
                  padding: '12px',
                  border: `2px solid ${!selectedTemplate ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                  background: !selectedTemplate ? 'var(--primary-light)' : 'white',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14 }}>Blank Team</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Start fresh with no agents
                </div>
              </div>
              {templates.map((t) => (
                <div
                  key={t.id}
                  onClick={() => setSelectedTemplate(t)}
                  style={{
                    padding: '12px',
                    border: `2px solid ${selectedTemplate?.id === t.id ? 'var(--primary)' : 'var(--border)'}`,
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    background:
                      selectedTemplate?.id === t.id ? 'var(--primary-light)' : 'white',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                  {t.description && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {t.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => setStep(1)}>Next</Button>
            </div>
          </div>
        )}

        {/* Step 1: Configure */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              Configure team
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>Team name</label>
                <input
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder={selectedTemplate?.name ?? 'My Team'}
                  style={{
                    padding: '9px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)',
                    fontSize: 14,
                    outline: 'none',
                  }}
                />
              </div>
              {selectedTemplate?.roles && selectedTemplate.roles.length > 0 && (
                <div
                  style={{
                    padding: '10px 12px',
                    background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius)',
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                  }}
                >
                  Template: {selectedTemplate.name}
                  <div style={{ marginTop: 6 }}>
                    Roles:{' '}
                    {selectedTemplate.roles.map((r) => (
                      <Badge key={r} variant="info">{r}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent configurations */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>
                    Agents ({agentConfigs.length})
                  </span>
                  <Button size="sm" variant="secondary" onClick={addAgent}>
                    + Add Agent
                  </Button>
                </div>

                {agentConfigs.length === 0 && (
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      padding: '10px 12px',
                      border: '1px dashed var(--border)',
                      borderRadius: 'var(--radius)',
                      textAlign: 'center',
                    }}
                  >
                    No agents yet — click "+ Add Agent" to configure
                  </div>
                )}

                {agentConfigs.map((cfg, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      marginBottom: 8,
                      background: 'var(--bg-secondary)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 8,
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: 'var(--text-muted)' }}>
                        Agent {idx + 1}
                      </span>
                      <button
                        onClick={() => removeAgent(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          fontSize: 16,
                          padding: '0 4px',
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>
                            Name *
                          </label>
                          <input
                            value={cfg.displayName}
                            onChange={(e) => updateAgent(idx, { displayName: e.target.value })}
                            placeholder="e.g. Alice"
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius)',
                              fontSize: 13,
                              outline: 'none',
                              background: 'white',
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>
                            Role
                          </label>
                          <input
                            value={cfg.role}
                            onChange={(e) => updateAgent(idx, { role: e.target.value })}
                            placeholder="e.g. Frontend Dev"
                            style={{
                              width: '100%',
                              padding: '7px 10px',
                              border: '1px solid var(--border)',
                              borderRadius: 'var(--radius)',
                              fontSize: 13,
                              outline: 'none',
                              background: 'white',
                            }}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>
                          Model
                        </label>
                        <select
                          value={cfg.llmValue}
                          onChange={(e) => updateAgent(idx, { llmValue: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '7px 10px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius)',
                            fontSize: 13,
                            outline: 'none',
                            background: 'white',
                            cursor: 'pointer',
                          }}
                        >
                          {LLM_MODEL_OPTIONS.map((opt) => (
                            <option key={`${opt.provider}|${opt.model}`} value={`${opt.provider}|${opt.model}`}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setStep(0)}>
                Back
              </Button>
              <Button onClick={() => setStep(2)}>Next</Button>
            </div>
          </div>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              Confirm
            </h2>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Row label="Team" value={teamName || selectedTemplate?.name || 'New Team'} />
                <Row label="Template" value={selectedTemplate?.name ?? 'Blank'} />
                <Row label="Agents" value={agentConfigs.filter(c => c.displayName.trim()).length > 0
                  ? agentConfigs.filter(c => c.displayName.trim()).map(c => c.displayName).join(', ')
                  : 'None'} />
              </div>
            </Card>
            {agentConfigs.filter(c => c.displayName.trim()).length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                  AGENT DETAILS
                </div>
                {agentConfigs.filter(c => c.displayName.trim()).map((cfg, idx) => {
                  const modelOpt = LLM_MODEL_OPTIONS.find(o => `${o.provider}|${o.model}` === cfg.llmValue);
                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        marginBottom: 6,
                        fontSize: 12,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{cfg.displayName}</span>
                      {cfg.role && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>· {cfg.role}</span>}
                      <span
                        style={{
                          marginLeft: 8,
                          padding: '2px 6px',
                          background: 'var(--primary-light)',
                          borderRadius: 4,
                          color: 'var(--primary)',
                          fontSize: 11,
                        }}
                      >
                        {modelOpt?.label ?? cfg.llmValue}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Button variant="secondary" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={onConfirm} disabled={creating}>
                {creating ? 'Creating…' : 'Create team'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-muted)', width: 80 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: 'var(--text)' }}>{value}</span>
    </div>
  );
}
